import type { UserPrivate } from '@repo/types/users';

import { ChevronDownIcon, ChevronsUpDownIcon, PlusIcon, UserIcon } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button, ButtonGroup } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getAvatar } from '@/utils/getAvatar';

interface ParentLinkPickerProps {
  availableParents: UserPrivate[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectParent: (parent: UserPrivate) => void;
  onCreateNewParent: () => void;
}

export function ParentLinkPicker({ availableParents, open, onOpenChange, onSelectParent, onCreateNewParent }: ParentLinkPickerProps) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <ButtonGroup className={'w-full'}>
        <PopoverTrigger asChild>
          <Button type={'button'} variant={'outline'} role={'combobox'} className={'grow justify-between'}>
            <span className={'flex items-center gap-2'}>
              <PlusIcon className={'size-4'} />
              Powiąż rodzica
            </span>
            <ChevronsUpDownIcon className={'text-muted-foreground'} />
          </Button>
        </PopoverTrigger>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type={'button'} variant={'outline'} aria-label={'Więcej opcji dodawania rodzica'} className={'group'}>
              <ChevronDownIcon className={'transition-transform duration-200 group-data-[state=open]:rotate-180'} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side={'top'} align={'end'} onCloseAutoFocus={(event) => event.preventDefault()}>
            <DropdownMenuGroup>
              <DropdownMenuItem onSelect={() => onOpenChange(true)}>
                <div className="space-y-0.5">
                  <p>Powiąż istniejącego rodzica</p>
                  <p className="text-muted-foreground ms-1 text-xs">Wybierz rodzica z listy istniejących kont</p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onCreateNewParent}>
                <div className="space-y-0.5">
                  <p>Utwórz nowego rodzica</p>
                  <p className="text-muted-foreground ms-1 text-xs">Załóż nowe konto rodzica i powiąż je z uczniem</p>
                </div>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </ButtonGroup>
      <PopoverContent className={'min-w-xs p-0'} side={'top'} align={'start'} disablePortal>
        <Command>
          <CommandInput placeholder={'Szukaj rodzica...'} />
          <CommandList>
            <CommandEmpty>{availableParents.length === 0 ? 'Brak dostępnych rodziców.' : 'Nie znaleziono rodzica.'}</CommandEmpty>
            <CommandGroup>
              {availableParents.map((parent) => (
                <CommandItem key={parent.id} value={`${parent.name} ${parent.surname} ${parent.email}`} onSelect={() => onSelectParent(parent)} className="h-min">
                  <Avatar size={'sm'}>
                    <AvatarImage src={parent.avatarURL ? getAvatar(parent.avatarURL) : undefined} />
                    <AvatarFallback>
                      <UserIcon className={'size-3.5'} />
                    </AvatarFallback>
                  </Avatar>
                  <div className={'flex min-w-0 flex-col'}>
                    <p className={'truncate text-sm'}>
                      {parent.name} {parent.surname}
                    </p>
                    <p className={'text-text-tertiary truncate text-xs'}>{parent.email}</p>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
